import { useCallback, useRef, useState } from 'react';
import {
  Bold,
  Code,
  Eye,
  Heading1,
  Heading2,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Pencil,
} from 'lucide-react';
import { uploadLeadFile } from '../../../lib/leadsApi';
import { useUploadQueue } from '../../../hooks/useUploadQueue';
import { UploadChipList } from '../../ui/UploadProgress';
import { MarkdownView } from '../../tasks/linear/MarkdownView';

/**
 * CLE-192 — markdown rich-text editor for the lead description.
 *
 * Edit/Preview toggle, a formatting toolbar (headings, bold, italic, lists,
 * code) and inline image upload via the lead signed-URL flow — toolbar button,
 * drag-and-drop, and clipboard paste. Mirrors the task description editor but
 * uploads against the lead attachment endpoint.
 */

interface LeadMarkdownEditorProps {
  leadId: string;
  value: string;
  placeholder?: string;
  onSave: (next: string) => void;
  isSaving?: boolean;
}

type Mode = 'edit' | 'preview';

export function LeadMarkdownEditor({
  leadId,
  value,
  placeholder = 'Adaugă o descriere… Markdown acceptat.',
  onSave,
  isSaving,
}: LeadMarkdownEditorProps) {
  const [mode, setMode] = useState<Mode>('preview');
  const [draft, setDraft] = useState(value);
  // One chip per image being uploaded, each with its own bar — pasting a
  // screenshot used to give only a spinner in the toolbar.
  const { uploads, isUploading, run } = useUploadQueue();
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const surround = useCallback(
    (before: string, after = before) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const sel = draft.slice(start, end);
      const next = draft.slice(0, start) + before + sel + after + draft.slice(end);
      setDraft(next);
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = start + before.length;
        el.selectionEnd = start + before.length + sel.length;
      });
    },
    [draft],
  );

  const prefixLines = useCallback(
    (prefix: string) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const lineStart = draft.lastIndexOf('\n', start - 1) + 1;
      const block = draft.slice(lineStart, end);
      const prefixed = block
        .split('\n')
        .map((l) => (l.startsWith(prefix) ? l : prefix + l))
        .join('\n');
      const next = draft.slice(0, lineStart) + prefixed + draft.slice(end);
      setDraft(next);
      requestAnimationFrame(() => el.focus());
    },
    [draft],
  );

  const insertAtCaret = useCallback(
    (text: string) => {
      const el = textareaRef.current;
      const pos = el ? el.selectionStart : draft.length;
      setDraft((d) => d.slice(0, pos) + text + d.slice(pos));
    },
    [draft],
  );

  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (list.length === 0) return;
      try {
        for (const file of list) {
          const att = await run(file, (onProgress) => uploadLeadFile(leadId, file, onProgress));
          insertAtCaret(`\n![${att.name}](${att.url})\n`);
        }
      } catch {
        insertAtCaret('\n_(încărcarea imaginii a eșuat)_\n');
      }
    },
    [leadId, insertAtCaret, run],
  );

  function save() {
    if (draft !== value) onSave(draft);
    setMode('preview');
  }

  const hasContent = value.trim().length > 0;

  if (mode === 'preview') {
    return (
      <div className="group/desc">
        {hasContent ? (
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setMode('edit');
            }}
            className="block w-full rounded-md px-1 py-1 text-left transition-colors hover:bg-white/[0.03]"
          >
            <MarkdownView content={value} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setMode('edit');
            }}
            className="flex w-full items-center gap-2 rounded-md px-1 py-2 text-left text-[14px] text-gray-400 transition-colors hover:bg-white/[0.03]"
          >
            <Pencil size={14} /> {placeholder}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border ${dragOver ? 'border-brand-600' : 'border-gray-200'} bg-card`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files.length) void handleUpload(e.dataTransfer.files);
      }}
    >
      <div className="flex items-center gap-0.5 border-b border-gray-200 px-1.5 py-1">
        <ToolbarBtn title="Titlu 1" onClick={() => prefixLines('# ')}>
          <Heading1 size={15} />
        </ToolbarBtn>
        <ToolbarBtn title="Titlu 2" onClick={() => prefixLines('## ')}>
          <Heading2 size={15} />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Aldin" onClick={() => surround('**')}>
          <Bold size={15} />
        </ToolbarBtn>
        <ToolbarBtn title="Cursiv" onClick={() => surround('*')}>
          <Italic size={15} />
        </ToolbarBtn>
        <ToolbarBtn title="Cod inline" onClick={() => surround('`')}>
          <Code size={15} />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Listă cu puncte" onClick={() => prefixLines('- ')}>
          <List size={15} />
        </ToolbarBtn>
        <ToolbarBtn title="Listă numerotată" onClick={() => prefixLines('1. ')}>
          <ListOrdered size={15} />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Încarcă imagine" onClick={() => fileInputRef.current?.click()}>
          {isUploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) void handleUpload(e.target.files);
            e.target.value = '';
          }}
        />
        <div className="ml-auto">
          <ToolbarBtn title="Previzualizare" onClick={() => setMode('preview')}>
            <Eye size={15} />
          </ToolbarBtn>
        </div>
      </div>

      <textarea
        ref={textareaRef}
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onPaste={(e) => {
          const imageItems = Array.from(e.clipboardData.items).filter((it) =>
            it.type.startsWith('image/'),
          );
          if (imageItems.length) {
            e.preventDefault();
            const files = imageItems.map((it) => it.getAsFile()).filter((f): f is File => !!f);
            void handleUpload(files);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value);
            setMode('preview');
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          }
        }}
        rows={Math.max(6, draft.split('\n').length + 1)}
        className="block w-full resize-y bg-transparent px-3 py-2.5 font-mono text-[13px] leading-relaxed text-gray-800 placeholder:text-gray-400 focus:outline-none"
      />

      {uploads.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          <UploadChipList uploads={uploads} />
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-2 py-1.5">
        <span className="mr-auto pl-1 text-[11px] text-gray-400">
          Trage, lipește sau încarcă imagini · ⌘/Ctrl+Enter pentru salvare
        </span>
        <button
          type="button"
          onClick={() => {
            setDraft(value);
            setMode('preview');
          }}
          className="rounded px-2 py-1 text-[12px] text-gray-500 hover:bg-gray-200"
        >
          Anulează
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isSaving}
          className="rounded bg-brand-600 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {isSaving ? 'Se salvează…' : 'Salvează'}
        </button>
      </div>
    </div>
  );
}

function ToolbarBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-4 w-px bg-gray-300" />;
}
